import { z } from 'zod'

/**
 * Where a signed-out visitor was heading, carried through sign-in.
 *
 * A link to a room, a course or an event is the most-shared thing this product
 * has, and until now opening one while signed out rendered the screen anyway:
 * the shell was skipped, every request answered 401, and the member read « Les
 * publications ne se chargent pas » over an empty page. It looked broken
 * because it was — but the account was fine and so was the link.
 *
 * So the route redirects to sign-in and brings them back afterwards. That
 * return path is the whole of the risk here, which is why it lives in its own
 * module with its own tests rather than inline in the router.
 *
 * ── Why the destination is checked, twice ────────────────────────────────
 * `?next=` is attacker-controlled: anyone can mail a member a link to our own
 * sign-in page carrying somebody else's destination. Unchecked, that is a
 * textbook open redirect — the victim signs in on the real site and is handed
 * to a copy of it. So the value is validated when the route parses it AND
 * again before it is followed, because those are two different moments and a
 * future caller could reach the second without passing the first.
 *
 * The rule is "a path on this site, and nothing else": one leading slash, no
 * scheme, no `//host`, no backslash that a browser would normalise into one,
 * no `..`. A fragment is dropped rather than refused — it never reaches a
 * server, and keeping it would mean parsing it.
 */
const SAFE_PATH = /^\/[A-Za-z0-9\-._~!$&'()*+,;=:@%/?]*$/

export function isSafeNextPath(path: string): boolean {
  return (
    path.length > 1 &&
    path.length <= 2000 &&
    SAFE_PATH.test(path) &&
    !path.startsWith('//') &&
    !path.includes('\\') &&
    !path.includes('..')
  )
}

/**
 * The login route's query state.
 *
 * `.catch(undefined)` for the reason the account links give: a mangled value
 * should land somebody on a working sign-in page, not a router error. A
 * destination we cannot vouch for is simply forgotten, and they go home.
 */
export const loginSearchSchema = z.object({
  next: z
    .string()
    .optional()
    .transform((value) => (value && isSafeNextPath(value) ? value : undefined))
    .catch(undefined),
})

/**
 * What to remember when turning a signed-out visitor away from a private page.
 *
 * `null` for the pages that are already public — sending somebody back to the
 * sign-in page they just left, or to a landing page they can read signed out,
 * is noise in the URL and a loop waiting to happen.
 */
export function nextFrom(pathname: string, search = ''): string | null {
  const candidate = `${pathname}${search}`
  return isSafeNextPath(candidate) ? candidate : null
}
